import React, {
  useMemo,
  useRef,
  useCallback,
  useState,
  useEffect
} from 'react';
import { Chip, emphasize, Tooltip, IconButton } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import CancelIcon from '@mui/icons-material/Cancel';
import { ClassNameMap } from '@mui/styles';
import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import RemoveCircleOutlineOutlinedIcon from '@mui/icons-material/RemoveCircleOutlineOutlined';
import { useIntl } from 'react-intl';
import { LabelValue } from 'graphql/types';
import { TimesheetLabel } from 'modules/timesheet-labels/labelSchema';
import { useFeatureFlag } from 'modules/common/hooks';
import { useMessages } from 'modules/intl';
import { useChipState } from '../../hooks/useChipState';
import { useIsSuggestion } from '../../hooks/useIsSuggestion';

type SimpleChipProps = {
  disablePortalOnTooltip?: boolean;
  className?: string;
  label?: TimesheetLabel | null;
  onChipClick?: (event: any) => void;
  handleDelete?: (label: TimesheetLabel) => void;
  disabled?: boolean;
  size?: 'medium' | 'small';
  deleteEnabled?: boolean;
  chipRef?: React.MutableRefObject<HTMLDivElement | null>;
  classes?: ClassNameMap;
  acceptSuggestion?: (value: LabelValue) => void;
};

const useChipStyles = makeStyles((theme) => ({
  root: {
    maxWidth: '100%',
    minWidth: '70px'
  },
  clickableColorPrimary: {
    color: theme.palette.timesheetLabel.chipClickableColor,
    backgroundColor: theme.palette.timesheetLabel.nonEmptylabel,
    '&:hover, &:focus': {
      background: emphasize(theme.palette.timesheetLabel.nonEmptylabel, 0.08)
    },
    '&:active': {
      background: emphasize(theme.palette.timesheetLabel.nonEmptylabel, 0.12)
    }
  },
  suggestionOutlinedSecondary: {
    color: theme.palette.timesheetLabel.nonEmptylabel,
    borderColor: theme.palette.timesheetLabel.nonEmptylabel,
    borderStyle: 'dashed'
  },
  outlinedSecondary: {
    color: theme.palette.error.main,
    borderColor: theme.palette.error.main,
    borderStyle: 'solid'
  },
  suggestionAccept: {
    color: theme.palette.success.dark
  },
  suggestionReject: {
    color: theme.palette.error.dark
  }
}));

const message = [
  'timesheetLabels.acceptSuggestion',
  'timesheetLabels.denySuggestion',
  'timesheetLabels.removeBtn'
];

export const SimpleChip: React.FC<SimpleChipProps> = ({
  className,
  label,
  onChipClick,
  handleDelete,
  disabled = false,
  size = 'medium',
  deleteEnabled = true,
  chipRef,
  disablePortalOnTooltip,
  classes: overrideClasses,
  acceptSuggestion
}: SimpleChipProps) => {
  const isTimeSignalFederatedSuggestionsEnabled = useFeatureFlag(
    'isTimeSignalFederatedSuggestionsEnabled',
    false
  );
  const isSuggestion = useIsSuggestion(label);
  const classes = useChipStyles({ classes: overrideClasses });
  const {
    isEmptyValue: isEmpty,
    chipLabel,
    toolTipText,
    isDisabled
  } = useChipState(label, disabled);
  const chipClasses = useMemo(() => {
    const cls: ClassNameMap = {
      root: classes.root,
      clickableColorPrimary: classes.clickableColorPrimary
    };
    cls['outlinedSecondary'] =
      isSuggestion && !isEmpty
        ? classes.suggestionOutlinedSecondary
        : classes.outlinedSecondary;

    return cls;
  }, [classes, isSuggestion, isEmpty]);
  const clickableChiptestID = useMemo(
    () => `chip${onChipClick ? '_click' : ''}_${label?.name}_${label?.id}`,
    [onChipClick, label]
  );

  const chipParentRef = useRef<HTMLDivElement>(null);
  const [focusElement, setFocusElement] = useState({
    isFocus: false,
    width: 0
  });

  const mouseEnter = useCallback(() => {
    setFocusElement({
      isFocus: true,
      width: chipParentRef.current?.clientWidth ?? 0
    });
  }, [setFocusElement]);

  const mouseLeave = useCallback(() => {
    setFocusElement({ isFocus: false, width: 0 });
  }, [setFocusElement]);

  const onFocusChipWidth = useMemo(() => {
    return focusElement.isFocus && !isEmpty
      ? { style: { width: focusElement.width } }
      : null;
  }, [focusElement, isEmpty]);

  const onDelete = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      event.stopPropagation();
      if (label && handleDelete) handleDelete(label);
    },
    [handleDelete, label]
  );

  const onChipDelete = useMemo(() => {
    return focusElement.isFocus && !isEmpty && deleteEnabled
      ? {
          onDelete: onDelete
        }
      : null;
  }, [focusElement.isFocus, onDelete, isEmpty, deleteEnabled]);

  const onAcceptSuggestion = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      event.stopPropagation();
      const value = isTimeSignalFederatedSuggestionsEnabled
        ? label?.suggestedValues?.[0]
        : label?.value;
      if (value) {
        acceptSuggestion?.(value);
      }
    },
    [
      acceptSuggestion,
      isTimeSignalFederatedSuggestionsEnabled,
      label?.suggestedValues,
      label?.value
    ]
  );

  useEffect(() => {
    if (chipParentRef) {
      mouseLeave();
    }
  }, [chipParentRef, label, mouseLeave]);

  const isTiwScreenReaderEnabled = useFeatureFlag('isTiwScreenReaderEnabled');
  const messageLabel = useMessages(message);

  const deleteIcon = useMemo(
    () =>
      isSuggestion && !isEmpty ? (
        <>
          <IconButton
            size="small"
            data-testid={`accept-icon-button-${clickableChiptestID}`}
            onClick={onAcceptSuggestion}
            aria-label={
              isTiwScreenReaderEnabled
                ? messageLabel['timesheetLabels.acceptSuggestion']
                : undefined
            }
          >
            <CheckCircleOutlineOutlinedIcon
              fontSize="small"
              className={classes.suggestionAccept}
            />
          </IconButton>
          <IconButton
            size="small"
            data-testid={`reject-icon-button-${clickableChiptestID}`}
            onClick={onDelete}
            aria-label={
              isTiwScreenReaderEnabled
                ? messageLabel['timesheetLabels.denySuggestion']
                : undefined
            }
          >
            <RemoveCircleOutlineOutlinedIcon
              fontSize="small"
              className={classes.suggestionReject}
            />
          </IconButton>
        </>
      ) : (
        <IconButton
          size="small"
          data-testid={`clear-icon-button-${clickableChiptestID}`}
          aria-label={
            isTiwScreenReaderEnabled
              ? messageLabel['timesheetLabels.removeBtn']
              : undefined
          }
        >
          <CancelIcon fontSize={'small'} />
        </IconButton>
      ),
    [
      isSuggestion,
      isEmpty,
      clickableChiptestID,
      onAcceptSuggestion,
      isTiwScreenReaderEnabled,
      messageLabel,
      classes.suggestionAccept,
      classes.suggestionReject,
      onDelete
    ]
  );

  const hoverForDeleteProps = useMemo(
    () =>
      deleteEnabled
        ? {
            onMouseEnter: mouseEnter,
            onMouseLeave: mouseLeave,
            onFocus: mouseEnter,
            ...onFocusChipWidth
          }
        : undefined,
    [deleteEnabled, mouseEnter, mouseLeave, onFocusChipWidth]
  );

  const { formatMessage } = useIntl();
  const timesheetButtonMsg = formatMessage(
    {
      id: 'timesheetLabels.editTimesheetLable'
    },
    { value: chipLabel }
  );

  return (
    <div
      ref={chipParentRef}
      {...hoverForDeleteProps}
      data-testid={`${clickableChiptestID}-container`}
    >
      <Tooltip
        title={toolTipText}
        PopperProps={{ disablePortal: disablePortalOnTooltip }}
        disableInteractive
      >
        <Chip
          className={className}
          ref={chipRef}
          disabled={isDisabled}
          data-testid={clickableChiptestID}
          label={chipLabel}
          variant={!isEmpty && !isSuggestion ? 'filled' : 'outlined'}
          color={!isEmpty && !isSuggestion ? 'primary' : 'secondary'}
          size={size}
          clickable={!disabled}
          classes={chipClasses}
          onClick={onChipClick}
          {...onChipDelete}
          deleteIcon={deleteIcon}
          aria-label={isTiwScreenReaderEnabled ? timesheetButtonMsg : undefined}
        />
      </Tooltip>
    </div>
  );
};
